import { Component, Input, OnInit, OnDestroy, ElementRef, ViewEncapsulation } from '@angular/core';
import { WebConsoleConfigInterface } from './web-console-config.interface';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { WorkItemLens } from 'common/workitems/models/workitem-lens';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { WebConsoleAppInterface } from './web-console-container.interface';
import { EmbeddedWebConsoleAppEventHandlers } from './embedded-web-console-app-event-handlers';
import { LensEventHandlers } from './lens-event-handlers';
import { RemoteControlEventHandlers } from './remote-control-event-handlers';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { NotificationBarService } from 'shared/services/notification/notification-bar.service';
import { BcRestApi } from 'common/api/rest-api';
import { BcConfigService } from 'common/config/provider';
import { CommonAuthService } from 'common/auth/common-auth.service';

const loadScript = require('load-script');

@Component({
  selector: 'app-web-console-container',
  templateUrl: './web-console-container.component.html',
  styleUrls: ['./web-console-container.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WebConsoleContainerComponent implements OnInit, OnDestroy {
  @Input()
  workItem: ChatItem | WorkItemLens;

  @Input()
  webConsoleConfig: WebConsoleConfigInterface;

  private _embeddedApp: WebConsoleAppInterface = null;

  private _embeddedWebConsoleAppEventHandlers: EmbeddedWebConsoleAppEventHandlers;

  private _boundHandleNavigationChangeEvent: Function = null;

  private static externalScriptPromise: Promise<void>;

  constructor(private el: ElementRef,
              private RxEmitter: RxEmitter,
              private bcRestApi: BcRestApi,
              private bcNotificationService: NotificationBarService,
              private bcAnalytics: BcAnalytics,
              private commonAuth: CommonAuthService,
              bcConfig: BcConfigService) {
    this.commonAuth.isBoldEnabled().then(isBoldEnabled => {
      if (isBoldEnabled) {
        WebConsoleContainerComponent.loadExternalScript(bcConfig.current.webConsoleScriptUrl).catch(() => {
          this.bcAnalytics.eventTrack('WebConsoleScriptLoadError');
        });
      }
    });
  }

  public static loadExternalScript(webConsoleScriptUrl): Promise<void> {
    if (!WebConsoleContainerComponent.externalScriptPromise) {
      WebConsoleContainerComponent.externalScriptPromise = new Promise<void>((resolve, reject) => {
        loadScript(`${webConsoleScriptUrl}`, (lensUiErr) => {
          if (lensUiErr || !(<any>window).RescueWebConsole) {
            WebConsoleContainerComponent.externalScriptPromise = null;
            reject('LensUI failed to load: ' + lensUiErr);
          } else {
            resolve();
          }
        });
      });
    }
    return WebConsoleContainerComponent.externalScriptPromise;
  }

  ngOnInit() {
    WebConsoleContainerComponent.externalScriptPromise?.then(() => {
      this._embeddedApp = (<any>window).RescueWebConsole.embed(this.el.nativeElement.querySelector('.lens-module-container'), this.webConsoleConfig);
      this._createHandlersFromWorkItem(this.workItem);

      this._registerCommonEvents();
      this._embeddedWebConsoleAppEventHandlers.registerEvents();
    }).catch(() => {
      this.bcNotificationService.addNotification({ id: 'script-embed-failed', content: 'httpInterceptor.internalErrorMessage' });
      if ((<ChatItem>this.workItem).clearHistoryItem) {
        (<ChatItem>this.workItem).clearHistoryItem();
      }
      this.bcAnalytics.eventTrack('WebConsoleScriptEmbedError');
    });
  }

  ngOnDestroy() {
    if (this._embeddedApp) {
      this._unRegisterCommonEvents();
      this._embeddedWebConsoleAppEventHandlers.unregisterEvents();
      this._embeddedWebConsoleAppEventHandlers.onDestroyEvents();

      this._embeddedApp = null;
    }
  }

  public setFocus(lensContainer): void {
    lensContainer.focus();
  }

  private _createHandlersFromWorkItem(workItem: ChatItem | WorkItemLens) {
    if (this.workItem.isLens()) {
      this._embeddedWebConsoleAppEventHandlers = new LensEventHandlers(this._embeddedApp, this.RxEmitter);
    } else {
      this._embeddedWebConsoleAppEventHandlers = new RemoteControlEventHandlers(this._embeddedApp, this.RxEmitter, <ChatItem>workItem, this.bcRestApi);
    }
  }

  private _registerCommonEvents() {
    this._boundHandleNavigationChangeEvent = this._handleNavigationChangeEvent.bind(this);
    this._embeddedApp.ports.navigationChangeRequest.subscribe(this._boundHandleNavigationChangeEvent);
  }

  private _unRegisterCommonEvents() {
    this._embeddedApp.ports.navigationChangeRequest.unsubscribe(this._boundHandleNavigationChangeEvent);
  }

  private _handleNavigationChangeEvent(request: any) {
    this._embeddedApp.ports.navigationStringUpdated.send(request.newUrl);
  }
}
